import React from "react";
import { Star } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  STAR_COUNT,
  ratingBandInfo,
  ratingPercent,
  ratingStars,
  ratingTone,
  resolveScale,
} from "@/lib/ratingScale";

/**
 * Tone → colour, using the same semantic tokens as the rest of the employee UI.
 * --success / --warning are declared in index.css but not mapped in
 * tailwind.config.js, so they are reached through arbitrary values.
 */
const TONE_FILL = {
  positive: "bg-[hsl(var(--success))]",
  primary: "bg-primary",
  caution: "bg-[hsl(var(--warning))]",
  critical: "bg-destructive",
};

const TONE_CHIP = {
  positive: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]",
  primary: "bg-primary/10 text-primary",
  caution: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))]",
  critical: "bg-destructive/[0.14] text-destructive",
};

const TONE_STAR = {
  positive: "fill-[hsl(var(--success))] text-[hsl(var(--success))]",
  primary: "fill-primary text-primary",
  caution: "fill-[hsl(var(--warning))] text-[hsl(var(--warning))]",
  critical: "fill-destructive text-destructive",
};

/**
 * Proportional star gauge — `(rating / maxRating) × 5`, with the fractional
 * star partially filled so 4.02/10 reads as 2.01 stars rather than snapping
 * to 2. Kept for compact contexts; prefer {@link RatingMeter} where there is
 * room, since a fixed 5-star row implies a 5-point scale regardless of the
 * configured maximum.
 */
export const RatingStars = ({ rating, scale, className = "h-4 w-4" }) => {
  const filled = ratingStars(rating, scale);
  const tone = ratingTone(rating, scale);

  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {Array.from({ length: STAR_COUNT }, (_, index) => {
        // Portion of THIS star that should be filled, 0–1.
        const portion = Math.max(0, Math.min(1, filled - index));

        return (
          <span key={index} className="relative inline-flex">
            <Star className={cn(className, "text-muted-foreground/25")} />
            {portion > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${portion * 100}%` }}
              >
                <Star
                  className={cn(className, TONE_STAR[tone] ?? TONE_STAR.primary)}
                />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Progress toward the configured maximum.
 *
 * This is the scale-safe replacement for the fixed 5-star row: a bar is read as
 * "how far along", which stays true whether the admin sets the maximum to 5, 10
 * or anything else. The numeric value is always printed with its maximum so the
 * scale is explicit.
 *
 * Presentation only — it never rescales the rating, it just clamps to the cap
 * exactly as the server does.
 *
 * The BAR still fills to percent-of-max, which is what "how far along the scale
 * am I" means and stays true at any configuration. Its COLOUR, however, comes
 * from the base-anchored band — so an employee who is doing fine sees a green
 * bar even when it is only part-filled, instead of a red one that contradicts
 * the badge beside it.
 *
 * `scale` accepts a resolved scale, a raw `rating_config`, or a bare maximum.
 * Passing only a maximum falls back to the legacy 80% anchor rather than
 * producing nonsense, so an un-migrated caller degrades quietly.
 */
const RatingMeter = ({
  rating,
  scale,
  size = "md",
  showValue = true,
  showBand = true,
  digits = 3,
  className,
}) => {
  const resolved = resolveScale(scale);
  const { value, tone, label: band } = ratingBandInfo(rating, resolved);
  const max = resolved.max;
  const percent = ratingPercent(rating, resolved);

  const trackHeight = size === "sm" ? "h-1.5" : "h-2";
  const valueSize = size === "sm" ? "text-lg" : "text-2xl";

  return (
    <div className={cn("w-full", className)}>
      {(showValue || showBand) && (
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          {showValue && (
            <p className={cn("font-bold tabular-nums text-foreground", valueSize)}>
              {value.toFixed(digits)}
              <span className="ml-1 text-sm font-medium text-muted-foreground">
                / {max}
              </span>
            </p>
          )}
          {showBand && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-semibold",
                TONE_CHIP[tone] ?? TONE_CHIP.primary,
              )}
            >
              {band}
            </span>
          )}
        </div>
      )}

      <div
        className={cn("w-full overflow-hidden rounded-full bg-muted", trackHeight)}
        role="progressbar"
        aria-valuenow={Number(value.toFixed(digits))}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={`${value.toFixed(digits)} out of ${max} — ${band}`}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            TONE_FILL[tone] ?? TONE_FILL.primary,
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

export default RatingMeter;
